import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { formatBytes, containerFileTypes, TOAST_TYPE_ERROR } from "@Services/constants";
import React, { useEffect, useImperativeHandle, useState } from "react";
import { useDispatch } from "react-redux";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import IconComponent from "@CubeComponents/icons";
import FileManagerComponent from "@CubeComponents/FileManager/FileManagerComponent";
import _ from "lodash";
import { Box, Divider, Fade, FormControl, Menu, MenuItem, Select } from "@mui/material";
import CreateReviewModal from "@Features/reviews/CreateReviewModal";
import { FileConversionStatus } from "@Features/files";
import FileDelete from "./FileDelete/FileDelete";
import ExcelImport from "@Components/ExcelImport/ExcelImport";
import { excelImportSources } from "@Components/ExcelImport/ExcelHelper";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Text from "@CubeComponents/html/Text/Text";
import CreateTransmittal from "@Features/transmittals/pages/Transmitals/CreateTransmittal/CreateTransmittal";
import { convertDate } from "@Services/luxonHelper";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { getFileIcon, getIconbasedOnResourceType } from "@CubeComponents/constants/globalFunctions";
import { NomenclatureField } from "@Services/utils/useCustomFields";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import FileManagerAsPopup from "./FileManagerAsPopup";
import "./FileManagerNew.css";
import FolderSyncTemplate from "./FolderSyncTemplate";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import useFileManager from "./useFileManager";
import { showToastNotification } from "@Reducers/toastSlice";
import { hasInvalidChars } from "@Helpers/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { getSubColumns } from "@CubeComponents/html/SlickGrid1/customFields";
import { getStatusBtnStyle } from "@Features/reviews/helpers";
import Forbidden from "@Pages/Error/Forbidden";
import useUserModuleAccess from "@Services/userModuleAccessBySheetHook";
import ProjectModuleNoAccessIllustration from "@Components/ProjectModulesAccessIllustration";
import { PORTAL_NO_SEAT } from "helpers";
import { ArrowDropDownIcon } from "@mui/x-date-pickers/icons";

interface FileManagerProps {
  defaultSelectedTab?: string;
  fileManagerAsPopup?: boolean;
  spaceId: string;
  projectId: string;
  // Project-level ("Folders") surface: render every Space of the project as a tree root.
  projectMode?: boolean;
  showOnlyMergedFile?: boolean;
  setShowProperties?: any;
  mobileFileManager?: boolean;
  hideExternalSpace?: boolean;
  hideReviews?: boolean;
  hideAllTabs?: boolean;
  enableSpaceListDropdown?: boolean;
  openThisSpaceByDefault?: boolean;
  createShortcut?: boolean;
  showFolderTree?: boolean;
  handleFileSelectionCallback?: (files: any[]) => void;
  hideIssues?: boolean;
  hideTasks?: boolean;
  hideEstimates?: boolean;
  hideClashTests?: boolean;
  hideContracts?: boolean;
  hideSchedules?: boolean;
  useAttachedExternalVolume?: boolean;
  hideMergeFiles?: boolean;
  disableContextMenu?: boolean;
  disableFiles?: any[];
  enableOnlyShareableResources?: boolean;
  hideUpload?: boolean;
  enableFilesTypes?: string[] | null;
  hideShortcuts?: boolean;
  handleFolderSelectionCallback?: (folder: any) => void;
  setSpaceId?: (id: any) => void;
  alreadySelected?: any[] | null;
  showExcelImport?: boolean;
  defaultPath?: string;
  allowFolderSelectionCallback?: boolean;
  spaceList?: any[];
  disableDragAndDropUpload?: boolean;
  hideFilesWithoutVersion?: boolean;
}

export interface FileManagerHandle {
  updateRowCustomFields: (rowId: string, changes: Record<string, unknown>) => void;
}

const FileManager = React.forwardRef<FileManagerHandle, FileManagerProps>((props, ref) => {
  const dispatch = useDispatch<any>();
  const { disableFiles, hideFilesWithoutVersion } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const {
    fileManagerRef,
    formValidator,
    pollingRef,

    fileManagerData,
    setFileManagerData,
    showContainerImportModal,
    setShowContainerImportModal,
    importMode,
    openRenameModal,
    setOpenRenameModal,
    selectedFile,
    newName,
    setNewName,
    folderName,
    createReviewModal,
    openMergeModal,
    openMergeEstimateModal,
    openFolderCreationModal,
    openFileImport,
    openContainerModal,
    currentFolder,
    setCurrentFolder,
    selectedFilesToDelete,
    openDeleteConfirmationModal,
    openCreateTransmittalModal,
    setOpenCreateTransmittalModal,
    containerFormTemplate,
    fileTypeValue,
    resourceType,
    setResourceType,
    containerName,
    path,
    preSearchText,
    toolBarBtnState,
    anchorEl,
    open,
    importSubmenuAnchor,
    importSubmenuOpen,

    isFileVersionUploading,
    spaceData,
    handleDeleteModalClose,
    handleDeleteSuccess,
    getSelectedFilesInFileManager,
    getPermission,
    beforeSending,
    handleSelectOption,
    handleClick,
    handleClose,
    handleImportSubmenuOpen,
    handleImportSubmenuClose,
    closeReviewModal,
    onFileSelect,
    onFileClick,
    toolbarClick,
    onSuccess,
    saveFileMergeModal,
    closeFileMergeModal,
    closeFileImportPopup,
    closeMergeEstimateModal,
    handleFileImportCallback,
    createMergeEstimateModal,
    onFileLoad,
    handleModalClose,
    handleCreateContainer,
    handleSaveNewName,
    handleCloseFolder,
    handleFolderName,
    handleCustomFieldsData,
    handleEmbedUrl,
    handleContainerName,
    handleCreateFolder,
    handleNomenclatureChange,
    nomenclatureValues,
    menuOpen,
    setFileTypeValue,

    fileManagerOptionsMapper,
    dropDownButtonItems,

    fileManagerAsPopup,
    disableContextMenu,
    showFolderTree,
    disableDragAndDropUpload,
    spaceId,
    projectId,
    projectMode,
    reloadFileManager,
    layout,
    t,
  } = useFileManager({ ...props, navigate, location });

  // Seat/licence gate for the project-level "Folders" surface (same pattern as Contracts/Forms pages).
  const moduleAccess = useUserModuleAccess();

  useImperativeHandle(
    ref,
    () => ({
      // Navigate the file manager into a folder by id — used by the FileExplorer app-header breadcrumb.
      navigateToFolder: (folderId: string) => fileManagerRef.current?.navigateToFolder?.(folderId),
      updateRowCustomFields: (rowId: string, changes: Record<string, unknown>) => {
        if (!rowId || !changes) return;
        const grid: any = fileManagerRef.current;
        const slickGrid: any = grid?.slickGrid;

        const dataView: any = grid?.dataView ?? slickGrid?.getData?.();
        if (!slickGrid || !dataView) return;
        const id = String(rowId);
        const existing =
          typeof dataView.getItemById === "function"
            ? dataView.getItemById(id)
            : (dataView.getItems?.() ?? dataView.items ?? []).find((x: any) => String(x?.id) === id);
        if (!existing) return;
        const updated = _.cloneDeep(existing);
        for (const key of Object.keys(changes)) {
          _.set(updated, key, changes[key]);
        }
        if (typeof dataView.updateItem === "function") {
          dataView.updateItem(id, updated);
        }
        slickGrid.invalidate();
        slickGrid.render();
      },
    }),
    [fileManagerRef],
  );

  const fileManagerDataWithDisabled = React.useMemo(() => {
    const hasDisable = Array.isArray(disableFiles) && disableFiles.length > 0;
    if (!hasDisable && !hideFilesWithoutVersion) return fileManagerData;
    const disabledSet = new Set(hasDisable ? disableFiles : []);
    const tag = (file: any) => {
      const shouldDisable =
        hasDisable &&
        ((["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_MERGED_FILE"].includes(file?.resourceType) &&
          disabledSet.has(file.isEmptyContainer ? file.id : file.fileVersion)) ||
          (file?.resourceType === "RESOURCE_TYPE_ISSUE" && disabledSet.has(file.issueVersionId)) ||
          (file?.resourceType === "RESOURCE_TYPE_TASKS" && disabledSet.has(file.taskId)));
      return shouldDisable ? { ...file, isRowDisabled: true } : file;
    };
    const keep = (file: any) => {
      if (!hideFilesWithoutVersion) return true;
      if (file?.resourceType === "RESOURCE_TYPE_FILE" || file?.resourceType === "RESOURCE_TYPE_EMBED") {
        return !file?.isEmptyContainer && !!file?.fileVersion;
      }
      return true;
    };
    const transform = (arr: any[]) => arr.filter(keep).map(tag);
    return {
      ...fileManagerData,
      files: Array.isArray(fileManagerData?.files) ? transform(fileManagerData.files) : fileManagerData?.files,
      filteredFiles: Array.isArray(fileManagerData?.filteredFiles)
        ? transform(fileManagerData.filteredFiles)
        : fileManagerData?.filteredFiles,
    };
  }, [fileManagerData, disableFiles, hideFilesWithoutVersion]);

  const [customFieldColumns, setCustomFieldColumns] = useState<any[]>([]);

  useEffect(() => {
    const byFolder = fileManagerData?.customFieldsByFolder;
    const currentId = currentFolder?.id ?? "0";
    const templates: any[] = Array.isArray(byFolder?.[currentId])
      ? byFolder[currentId]
      : Array.isArray(fileManagerData?.customFields)
        ? fileManagerData.customFields
        : [];
    const seen = new Set<string>();
    const columns: any[] = [];
    templates.forEach((item: any) => {
      (item?.template ?? []).forEach((section: any) => {
        getSubColumns(section)?.forEach((col: any) => {
          if (col?.id && !seen.has(col.id)) {
            seen.add(col.id);
            columns.push(col);
          }
        });
      });
    });
    setCustomFieldColumns(columns);
  }, [fileManagerData?.customFieldsByFolder, fileManagerData?.customFields, currentFolder?.id]);

  const statusTemplate = (status: any) => {
    if (!status || status._id === null) return "-";
    const container = document.createElement("div");
    createRoot(container).render(
      <p className="w-fit rounded px-2 py-1 font-semibold text-white" style={getStatusBtnStyle(status.category)}>
        {status.name}
      </p>,
    );
    return container;
  };

  const nameTemplate = (args: any) => {
    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);
    let content: any = "";
    if (!args.isFormTemplateValid) {
      content = t("Mandatory custom fields not filled.");
    } else if (!args.isFileNomenclatureCorrect) {
      content = t("File name does not comply with File nomenclature set for this Folder. Please rename the file.");
    }
    root.render(
      <Box className="flex items-center flex-row gap-1 justify-between name-td">
        <div className="flex gap-2 items-center">
          {getIconbasedOnResourceType(args)}
          <Text hideToolTip>{args.name}</Text>
        </div>
        <Box className="flex gap-2">
          <>
            {(!args.isFileNomenclatureCorrect || !args.isFormTemplateValid) &&
              args.resourceType !== "RESOURCE_TYPE_TRANSMITTAL" &&
              !args.transmittalVersionId &&
              content && (
                <IconComponent
                  name={"ErrorIcon"}
                  position="bottom"
                  toolTipContent={content}
                  width="1rem"
                  height="1rem"
                />
              )}
            <FileConversionStatus ref={pollingRef} {...args} />
          </>
        </Box>
      </Box>,
    );
    return container;
  };

  const fileColumns: any[] = [
    {
      id: "name",
      field: "name",
      name: t("Name"),
      minWidth: 250,
      filterType: "Text",
      formatter: (data: number, params: number, a: any, b: unknown, value: any) => nameTemplate(value),
    },
    {
      id: "dateModified",
      field: "dateModified",
      name: t("Last Modified on"),
      filterType: "Date",
      formatter: (data: number, params: number, a: any, b: unknown, value: any) =>
        convertDate(_.get(value, "dateModified"), "dateForTooltip"),
      minWidth: 150,
    },
    {
      id: "modifiedBy",
      field: "modifiedBy",
      filterType: "Text",
      name: t("Last Modified by"),
      minWidth: 150,
    },
    {
      id: "size",
      field: "size",
      name: t("Size"),
      minWidth: 80,
      filterType: "Number",
      _valueGetter: (value) => (value.size && value.size != "0" ? formatBytes(value.size) : "-"),
      formatter: (data: number, params: number, a: any, b: unknown, value: any) =>
        value.size && value.size != "0" ? formatBytes(value.size) : "-",
    },
    {
      id: "versionNumber",
      field: "versionNumber",
      name: t("Version"),
      filterType: "Number",
      formatter: (data: number, params: number, a: any, b: unknown, value: any) =>
        value.versionNumber ? `V${value.versionNumber}` : "",
      minWidth: 80,
    },
    {
      id: "revision",
      field: "revision",
      name: t("Revision"),
      filterType: "Number",
      initiallyHidden: true,
      formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) =>
        value?.revisionSeries?.label || "-",
      minWidth: 100,
    },
    {
      id: "statusInReviews",
      field: "statusInReviews",
      filterType: "Text",
      name: t("Status"),
      minWidth: 170,
      initiallyHidden: true,
      _valueGetter: (row: any) => row?.statusInReviews?.name || "",
      formatter: (_data: number, _params: number, _a: any, _b: unknown, value: any) =>
        statusTemplate(value?.statusInReviews),
    },
  ];

  const getUploadDropDown = () => {
    const permissionCheck = getPermission();
    return (
      <Box className="border ms-5 border-button_light rounded">
        <ButtonComponent
          onClick={handleClick}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          endIcon={<ArrowDropDownIcon />}
        >
          <span className="flex items-center gap-2">
            <IconComponent name="add" width="1rem" height="1rem" />
            <Text data-testid="addNewFileBtn">{t("New")}</Text>
          </span>
        </ButtonComponent>
        <Menu
          id="fade-menu"
          slotProps={{
            list: {
              "aria-labelledby": "fade-button",
            },
          }}
          slots={{ transition: Fade }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          {dropDownButtonItems.map((item: any) =>
            item.seperator ? (
              <Divider key={item.id} sx={{ margin: "0px !important" }} />
            ) : item.hasSubmenu ? (
              <MenuItem
                sx={{ fontSize: "0.875rem", paddingBottom: "0.5rem !important", paddingTop: "0.5rem !important" }}
                key={item.id}
                onMouseEnter={handleImportSubmenuOpen}
                onClick={handleImportSubmenuOpen}
              >
                <span className="flex items-center gap-2 w-full justify-between">
                  <span className="flex items-center gap-2">
                    <IconComponent name={item.iconName} width="1rem" height="1rem" />
                    {item.text}
                  </span>
                  <ChevronRightIcon fontSize="small" />
                </span>
              </MenuItem>
            ) : (
              <MenuItem
                sx={{ fontSize: "0.875rem", paddingBottom: "0.5rem !important", paddingTop: "0.5rem !important" }}
                key={item.id}
                onClick={(args) => {
                  if (isFileVersionUploading) {
                    dispatch(
                      showToastNotification({
                        content: t("Please close other upload operations before proceeding!"),
                        type: TOAST_TYPE_ERROR,
                      }),
                    );
                  } else {
                    if (!permissionCheck) {
                      dispatch(
                        showToastNotification({
                          content: t("Insufficient permission"),
                          type: TOAST_TYPE_ERROR,
                        }),
                      );
                      return;
                    }
                    handleSelectOption({ ...args, item });
                  }
                  handleClose();
                }}
              >
                <span className="flex items-center gap-2">
                  <IconComponent name={item.iconName} width="1rem" height="1rem" />
                  {item.text}
                </span>
              </MenuItem>
            ),
          )}
        </Menu>
        {/* Import submenu */}
        <Menu
          anchorEl={importSubmenuAnchor}
          open={importSubmenuOpen}
          onClose={handleImportSubmenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          MenuListProps={{ onMouseLeave: handleImportSubmenuClose }}
        >
          {dropDownButtonItems
            .find((item: any) => item.id === "import")
            ?.subItems?.map((subItem: any) => (
              <MenuItem
                sx={{ fontSize: "0.875rem", paddingBottom: "0.5rem !important", paddingTop: "0.5rem !important" }}
                key={subItem.id}
                onClick={(args) => {
                  if (isFileVersionUploading) {
                    dispatch(
                      showToastNotification({
                        content: t("Please close other upload operations before proceeding!"),
                        type: TOAST_TYPE_ERROR,
                      }),
                    );
                  } else {
                    if (!permissionCheck) {
                      dispatch(
                        showToastNotification({
                          content: t("Insufficient permission"),
                          type: TOAST_TYPE_ERROR,
                        }),
                      );
                      return;
                    }
                    handleSelectOption({ ...args, item: subItem });
                  }
                  handleClose();
                }}
              >
                <span className="flex items-center gap-2">
                  <IconComponent name={subItem.iconName} width="1rem" height="1rem" />
                  {subItem.text}
                </span>
              </MenuItem>
            ))}
        </Menu>
      </Box>
    );
  };

  const getToolBarItems = () => {
    let toolbar: any[] = [];
    if (fileManagerAsPopup) {
      toolbar = [];
    } else {
      toolbar = [
        {
          name: "Upload",
          id: "upload",
          align: "left",
          disabled: !toolBarBtnState.upload,
          show: true,
          template: getUploadDropDown,
        },
        {
          name: t("Delete"),
          id: "delete",
          prefixIcon: "delete",
          align: "left",
          disabled: !toolBarBtnState.delete,
          show: true,
          ["data-testid"]: "deleteFileOrFolder",
        },
        {
          name: t("Rename"),
          id: "rename",
          prefixIcon: "edit",
          align: "left",
          disabled: !toolBarBtnState.rename,
          show: true,
          ["data-testid"]: "renameFileOrFolder",
        },
        {
          name: t("Download"),
          id: "download",
          prefixIcon: "download",
          align: "left",
          disabled: !toolBarBtnState.download,
          show: true,
          ["data-testid"]: "downloadFileOrFolder",
        },
        {
          show: (window as any).electronAPI,
          align: "right",
          template: () => (
            <FolderSyncTemplate
              spaceId={spaceId}
              projectId={projectId}
              spaceData={spaceData}
              currentFolder={currentFolder}
            />
          ),
        },
      ];
    }
    return toolbar;
  };

  const toolbarItems = getToolBarItems();

  const renderFileManager = () => (
    <FileManagerComponent
      contextMenuSettings={{
        file: fileManagerOptionsMapper.fileOptions,
        folder: fileManagerOptionsMapper.folderOptions,
        layout: fileManagerOptionsMapper.layoutOptions,
        visible: !disableContextMenu,
      }}
      disableDragAndDropUpload={disableDragAndDropUpload}
      menuOpen={menuOpen}
      path={path!}
      showFolderTree={showFolderTree}
      beforeSending={beforeSending}
      onFileLoad={onFileLoad}
      onSuccess={onSuccess}
      allowDragAndDropUpload={!layout?.FILE_MANAGER?.IMPORT_ONLY_UPLOADS}
      fileColumns={[...fileColumns, ...customFieldColumns]}
      setCurrentFolder={setCurrentFolder}
      ref={fileManagerRef}
      toolbarClick={toolbarClick}
      toolbarItems={toolbarItems}
      allowViewFileOptions={true}
      fileManagerData={fileManagerDataWithDisabled}
      setFileManagerData={setFileManagerData}
      onFileClick={onFileClick}
      onFileSelect={onFileSelect}
      preSearchText={preSearchText ?? undefined}
      projectMode={projectMode}
      projectId={projectId}
    />
  );

  const getProjectContent = () => {
    // Seat / license gate takes precedence over everything.
    if (moduleAccess.ready && moduleAccess.portalSeat === PORTAL_NO_SEAT) {
      return <ProjectModuleNoAccessIllustration />;
    }
    const spaceRoots = (fileManagerData?.files || []).filter((f: any) => f?.isSpaceRoot);
    const hasAccessibleSpace = spaceRoots.some((f: any) => f?.permission?.read);
    // Once the project read has finished, if the user can access no Space (all locked, or the project has
    // none), show the "not part of any Space" illustration — never the tree / a project-as-cwd fallback.
    if (fileManagerData?.projectLoaded && !hasAccessibleSpace) {
      return (
        <Box className="flex flex-col w-full h-full">
          {/* "Folders" label pinned at the top, centered */}
          <div className="flex gap-2 w-full justify-center text-2xl text-text_light">
            <IconComponent name={"folder"} color="#666A84" showGradient width="2rem" height="2rem" />
            {t("Folders")}
          </div>
          <Box className="flex flex-1 flex-col gap-4 items-center justify-center">
            <IconComponent name="NoData" width="18.75rem" height="18.8125rem" />
            <Box className="font-semibold text-lg">
              <Text>{t("You're not part of any Space.")}</Text>
            </Box>
            <Text className="text-sm text-gray-500 text-center">
              {t(
                "You need to be part of at least one Space to work on this module. Please contact your Project Manager.",
              )}
            </Text>
          </Box>
        </Box>
      );
    }
    const lockedRootSelected = currentFolder?.isSpaceRoot && currentFolder?.permission?.read === false;
    return (
      <div className="w-full h-full relative flex">
        {renderFileManager()}
        {lockedRootSelected && (
          <Box className="flex flex-col gap-4 absolute bg-white top-0 bottom-0 end-0 z-20 items-center justify-center w-[70%]">
            <IconComponent name="NoAccess" width="12rem" height="12rem" />
            <Box className="font-semibold text-lg">
              <Text>{t("No access")}</Text>
            </Box>
            <Text className="text-sm text-gray-500 text-center">
              {t("You do not have access to the root level of this Space or any files and folders inside it.")}
            </Text>
          </Box>
        )}
      </div>
    );
  };

  const getContent = () => {
    if (projectMode) {
      return getProjectContent();
    }
    if (spaceData?.noAccess) {
      return <Forbidden />;
    } else if (spaceData?.id) {
      return <>{renderFileManager()}</>;
    } else {
      return "";
    }
  };

  const newContainerCreateModal = () => (
    <Box className="w-full">
      <Box className="my-4">
        <Text>{t("Format")}</Text>
        <Box className="mt-2">
          <FormControl
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: "2rem",
              },
            }}
          >
            <Select
              value={resourceType?.text}
              onChange={(e) => {
                setFileTypeValue("");
                setResourceType({ text: (e.target as any).text, value: e.target.value });
              }}
            >
              <MenuItem value="RESOURCE_TYPE_FILE">
                <Text>{t("File")}</Text>
              </MenuItem>
              <MenuItem value="RESOURCE_TYPE_EMBED">
                <Text>{t("Embed")}</Text>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box className="my-4">
        <Text> {t("Name")} </Text>
        {currentFolder?.fileNomenclature?.fields?.length ? (
          <Box className="mt-2 flex gap-2">
            {currentFolder.fileNomenclature.fields.map((field: any, index: number) => (
              <NomenclatureField
                key={`${field._id}-${index}`}
                args={{ id: 1 }}
                field={field}
                index={index}
                value={nomenclatureValues[index] ?? ""}
                handleNomenclatureChange={handleNomenclatureChange}
              />
            ))}
          </Box>
        ) : (
          <Box className="mt-2">
            <TextFieldComponent onChange={handleContainerName} placeholder={t("Container Name")} />
          </Box>
        )}
      </Box>
      {resourceType.value == "RESOURCE_TYPE_EMBED" && (
        <Box className="my-4">
          <Text>{t("URL")}</Text>
          <Box className="mt-2">
            <TextFieldComponent onChange={handleEmbedUrl} placeholder={t("Embed URL")} />
          </Box>
        </Box>
      )}
      {resourceType.value == "RESOURCE_TYPE_FILE" && (
        <>
          <Box className="my-4">
            <Text>{t("File Type")}</Text>
            <Box className="mt-2">
              <AutoComplete
                options={containerFileTypes.map((x: string) => ({ text: x }))}
                valueField="text"
                getOptionLabel={(option) => option?.text || ""}
                value={
                  containerFileTypes.map((x: string) => ({ text: x })).find((opt: any) => opt.text === fileTypeValue) ||
                  null
                }
                onChange={(_event, newValue) => {
                  setFileTypeValue(newValue?.text ?? null);
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} className="flex items-center gap-1 mt-1">
                    <span className="mb-1 flex">{getFileIcon(option.text)}</span>
                    <span>
                      <Text>{option.text}</Text>
                    </span>
                  </Box>
                )}
                renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select file Type")} />}
              />
            </Box>
          </Box>
          <Box className="my-4">
            {containerFormTemplate && (
              <FormPreview
                formData={{ template: containerFormTemplate }}
                disabled={false}
                allowPadding={false}
                allowBorder={false}
                fullWidthInput
                removeIcons
                handleData={handleCustomFieldsData}
                formValidator={formValidator}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const getRenameModal = () => (
    <ModalStateDialogComponent
      footerContent={
        <Box className="flex w-full gap-4">
          <ButtonComponent
            onClick={handleSaveNewName}
            disabled={!newName.trim() || hasInvalidChars(newName)}
            buttonType="gradientButton"
            data-testid="saveNewName"
          >
            <Text>{t("Save")}</Text>
          </ButtonComponent>
          <ButtonComponent
            onClick={() => {
              setOpenRenameModal(false);
              setNewName("");
            }}
            buttonType="borderButton"
            data-testid="cancelNewName"
          >
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </Box>
      }
      onCancel={() => {
        setOpenRenameModal(false);
      }}
      headerTitle={t("Rename")}
      modelContent={
        <div className="w-full flex flex-col gap-1">
          <TextFieldComponent
            onChange={(args) => setNewName(args.target.value)}
            placeholder={t("Enter a new name")}
            value={newName}
            data-testid="enterNewName"
          />
          {hasInvalidChars(newName) && (
            <Text className="text-red-600 whitespace-normal text-xs">
              {t(
                'The file or folder name contains invalid characters. Please use a different name. Valid file or folder name cannot end with a dot or space, and cannot contain any of the following characters: \\ / : * ? " < > |',
              )}
            </Text>
          )}
        </div>
      }
      isVisible={openRenameModal.state}
      width="25%"
      allowModalPadding
    />
  );

  const getFolderCreationModal = () => (
    <ModalStateDialogComponent
      footerContent={
        <Box className="flex w-full gap-4">
          <ButtonComponent
            onClick={handleCreateFolder}
            disabled={!folderName?.trim() || hasInvalidChars(folderName)}
            buttonType="gradientButton"
            data-testid="createFolderBtn"
          >
            <Text>{t("Create")}</Text>
          </ButtonComponent>
          <ButtonComponent
            onClick={() => {
              handleCloseFolder();
            }}
            buttonType="borderButton"
            data-testid="discardFolderBtn"
          >
            <Text>{t("Discard")}</Text>
          </ButtonComponent>
        </Box>
      }
      onCancel={handleCloseFolder}
      headerTitle={t("New Folder")}
      modelContent={
        <div className="w-full flex flex-col gap-1">
          <TextFieldComponent
            onChange={handleFolderName}
            placeholder={t("Enter your folder name")}
            data-testid="enterFolderName"
          />
          {hasInvalidChars(folderName) && (
            <Text className="text-red-600 whitespace-normal text-xs">
              {t(
                'The file or folder name contains invalid characters. Please use a different name. Valid file or folder name cannot end with a dot or space, and cannot contain any of the following characters: \\ / : * ? " < > |',
              )}
            </Text>
          )}
        </div>
      }
      isVisible={openFolderCreationModal}
      width="25%"
      allowModalPadding
    />
  );

  const getContainerModal = () => (
    <ModalStateDialogComponent
      footerContent={
        <Box className="flex w-full gap-4">
          <ButtonComponent
            onClick={handleCreateContainer}
            disabled={containerName.trim() == "" || !fileTypeValue}
            buttonType="gradientButton"
          >
            <Text>{t("Create")}</Text>
          </ButtonComponent>
          <ButtonComponent
            onClick={() => {
              handleModalClose();
            }}
            buttonType="borderButton"
          >
            <Text>{t("Discard")}</Text>
          </ButtonComponent>
        </Box>
      }
      headerTitle={t("New Container")}
      onCancel={handleModalClose}
      modelContent={newContainerCreateModal()}
      isVisible={openContainerModal}
      width="60%"
      allowModalPadding
    />
  );

  const getFileManagerPopup = () => {
    const selectedFiles = getSelectedFilesInFileManager();

    if (openFileImport) {
      return (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={handleFileImportCallback}
          handleFileSelectedCancelClick={closeFileImportPopup}
          workspaceId={spaceId}
          id="fileManagerAsPopup"
          useAttachedExternalVolume
          disableContextMenu
          heading={t("Import Files")}
          hideAllTabs
          minimumFiles={0}
        />
      );
    } else if (openMergeEstimateModal) {
      return (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={createMergeEstimateModal}
          handleFileSelectedCancelClick={closeMergeEstimateModal}
          workspaceId={spaceId}
          id="fileManagerAsPopup"
          hideIssues
          hideTasks
          hideClashTests
          hideSchedules
          hideContracts
          heading={t("Merged Estimates")}
          allowInput
          inputLabel="Merged Estimate Name"
          minimumFiles={0}
          selectedFilesData={selectedFiles}
          createMerge
        />
      );
    } else if (openMergeModal) {
      return (
        <FileManagerAsPopup
          projectId={projectId}
          handleFileSelectedButtonClick={saveFileMergeModal}
          handleFileSelectedCancelClick={closeFileMergeModal}
          workspaceId={spaceId}
          id="fileManagerAsPopup"
          hideIssues
          hideTasks
          hideClashTests
          hideSchedules
          hideContracts
          hideReviews
          hideAllTabs
          heading={t("Merge")}
          allowInput
          inputLabel="Name"
          minimumFiles={0}
          selectedFilesData={selectedFiles}
          createMerge
          addEmptyContainerToList
        />
      );
    } else {
      return "";
    }
  };

  return (
    <Box className="w-full h-full flex relative bg-white flex-col" data-testid="filemanagerComponentRoot">
      {getContent()}
      {openDeleteConfirmationModal && (
        <FileDelete
          workspaceId={currentFolder?.spaceId ?? spaceId}
          selectedFiles={selectedFilesToDelete}
          onCloseModal={handleDeleteModalClose}
          onDeleteSuccess={handleDeleteSuccess}
          path={path}
        />
      )}
      {showContainerImportModal && (
        <ExcelImport
          showImportModal={showContainerImportModal}
          setShowImportModal={setShowContainerImportModal}
          source={excelImportSources.FILES}
          sourceId="a"
          refreshDataGrid={reloadFileManager}
          importMode={importMode}
          currentData={{
            projectId,
            hierarchyId: currentFolder.hierarchyId,
            parentId: currentFolder.hierarchyId,
            spaceId: currentFolder?.spaceId ?? spaceId,
            folderName: currentFolder.name,
            filterPath: currentFolder.filterPath,
          }}
        />
      )}
      {openRenameModal.state && getRenameModal()}
      {openCreateTransmittalModal && (
        <CreateTransmittal
          isOpen={openCreateTransmittalModal}
          onClose={() => setOpenCreateTransmittalModal(false)}
          projectId={projectId}
          selectedSpaceId={selectedFile?.[0]?.spaceId ?? currentFolder?.spaceId ?? spaceId}
          selectedReferences={selectedFile}
        />
      )}
      {createReviewModal.show && (
        <CreateReviewModal
          spaceId={currentFolder?.spaceId ?? spaceId}
          refreshGrid={reloadFileManager}
          closeModal={closeReviewModal}
          projectId={projectId}
          initialFiles={getSelectedFilesInFileManager()}
        />
      )}
      {openContainerModal && getContainerModal()}
      {openFolderCreationModal && getFolderCreationModal()}
      {(openFileImport || openMergeEstimateModal || openMergeModal) && getFileManagerPopup()}
    </Box>
  );
});

FileManager.displayName = "FileManager";

export default FileManager;
